// /app/frontend/src/pages/compliance/questionnaire/CampaignCreatePage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Plus, Trash2, Send, Save, Users, Building2,
  Mail, User, Briefcase, Info, Shield, KeyRound
} from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import { Badge } from '../../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../../components/ui/table';
import { Checkbox } from '../../../components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '../../../components/ui/radio-group';
import {
  Alert, AlertDescription
} from '../../../components/ui/alert';

import { useAuth } from '../../../contexts/AuthContext';
import { questionnaireAPI, entitiesAPI, clientsAPI } from '../../../services/api';

export default function CampaignCreatePage() {
  const navigate = useNavigate();
  const { user, isDPO } = useAuth();
  
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [entities, setEntities] = useState([]);
  const [clients, setClients] = useState([]);
  const [template, setTemplate] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    client_id: '',
    entity_id: '',
    include_partial_responses: false,
    auth_method: 'otp',
  });
  
  const [recipients, setRecipients] = useState([
    { email: '', name: '', department: '' }
  ]);
  
  const [bulkInput, setBulkInput] = useState('');
  const [showBulkInput, setShowBulkInput] = useState(false);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      
      // Load template
      const templateRes = await questionnaireAPI.getDefaultTemplate();
      setTemplate(templateRes.data);
      
      // Load entities
      if (isDPO) {
        const [entitiesRes, clientsRes] = await Promise.all([
          entitiesAPI.getAll(),
          clientsAPI.getAll()
        ]);
        setEntities(entitiesRes.data);
        setClients(clientsRes.data);
      } else {
        const entitiesRes = await entitiesAPI.getAll();
        setEntities(entitiesRes.data);
        if (entitiesRes.data.length > 0) {
          setFormData(prev => ({
            ...prev,
            entity_id: entitiesRes.data[0].id,
            client_id: entitiesRes.data[0].client_id
          }));
        }
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleEntityChange = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    setFormData(prev => ({
      ...prev,
      entity_id: entityId,
      client_id: entity?.client_id || prev.client_id
    }));
  };

  const addRecipient = () => {
    setRecipients([...recipients, { email: '', name: '', department: '' }]);
  };

  const removeRecipient = (index) => {
    if (recipients.length > 1) {
      setRecipients(recipients.filter((_, i) => i !== index));
    }
  };

  const updateRecipient = (index, field, value) => {
    const updated = [...recipients];
    updated[index][field] = value;
    setRecipients(updated);
  };

  const parseBulkInput = () => {
    // Parse CSV/line-separated emails
    const lines = bulkInput.split(/[\n,;]+/).map(l => l.trim()).filter(l => l);
    const newRecipients = lines.map(line => {
      const parts = line.split(/[,\t]+/).map(p => p.trim());
      return {
        email: parts[0] || '',
        name: parts[1] || '',
        department: parts[2] || ''
      };
    }).filter(r => r.email);
    
    if (newRecipients.length > 0) {
      setRecipients([...recipients.filter(r => r.email), ...newRecipients]);
      setBulkInput('');
      setShowBulkInput(false);
      toast.success(`Added ${newRecipients.length} recipients`);
    }
  };

  const validateForm = () => {
    if (!formData.name.trim()) {
      toast.error('Please enter a campaign name');
      return false;
    }
    if (!formData.entity_id) {
      toast.error('Please select an entity');
      return false;
    }
    
    const validRecipients = recipients.filter(r => r.email.trim());
    if (validRecipients.length === 0) {
      toast.error('Please add at least one recipient');
      return false;
    }
    
    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    for (const r of validRecipients) {
      if (!emailRegex.test(r.email)) {
        toast.error(`Invalid email: ${r.email}`);
        return false;
      }
    }
    
    return true;
  };

  const handleSave = async (send = false) => {
    if (!validateForm()) return;
    
    try {
      setSaving(true);
      
      const validRecipients = recipients.filter(r => r.email.trim());
      
      const campaignData = {
        template_id: template.id,
        client_id: formData.client_id,
        entity_id: formData.entity_id,
        name: formData.name,
        description: formData.description,
        include_partial_responses: formData.include_partial_responses,
        auth_method: formData.auth_method,
        recipients: validRecipients,
      };
      
      const response = await questionnaireAPI.createCampaign(campaignData);
      const campaignId = response.data.id;

      if (send) {
        // Pre-flight check before sending
        const preflight = await questionnaireAPI.preflightCampaign(campaignId);
        if (!preflight.data.can_send) {
          const names = preflight.data.conflicts.map(c => c.email).join(', ');
          toast.error(
            `Cannot send: these recipients have active guest sessions: ${names}. ` +
            `Revoke their access in Temp Guest Management, then send from the campaign page.`
          );
          // Navigate to the draft campaign so DPO can send manually after resolving
          navigate(`/compliance/questionnaires/campaign/${campaignId}`);
          return;
        }
        await questionnaireAPI.sendCampaign(campaignId);
        toast.success('Campaign created and sent! Guest accounts are being provisioned.');
      } else {
        toast.success('Campaign saved as draft');
      }

      navigate(`/compliance/questionnaires/campaign/${campaignId}`);
    } catch (error) {
      console.error('Error saving campaign:', error);
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to save campaign');
    } finally {
      setSaving(false);
    }
  };

  const validRecipientCount = recipients.filter(r => r.email.trim()).length;

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6 max-w-4xl mx-auto" data-testid="campaign-create-page">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/compliance/questionnaires')}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Create Campaign</h1>
            <p className="text-muted-foreground">
              Send TR2UST Staff Assessment to multiple recipients
            </p>
          </div>
        </div>

        {/* Template Info */}
        {template && (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription>
              <strong>{template.title}</strong> • {template.total_questions} questions • ~{template.estimated_time_minutes} min
            </AlertDescription>
          </Alert>
        )}

        {/* Campaign Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Campaign Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Campaign Name *</Label>
                <Input
                  id="name"
                  placeholder="e.g., Q1 2026 Staff Assessment"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="campaign-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="entity">Entity *</Label>
                <Select value={formData.entity_id} onValueChange={handleEntityChange}>
                  <SelectTrigger data-testid="entity-select">
                    <Building2 className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Select entity" />
                  </SelectTrigger>
                  <SelectContent>
                    {entities.map(e => (
                      <SelectItem key={e.id} value={e.id}>{e.entity_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                placeholder="Add any notes about this campaign..."
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="include_partial"
                checked={formData.include_partial_responses}
                onCheckedChange={(checked) => setFormData({ ...formData, include_partial_responses: checked })}
              />
              <Label htmlFor="include_partial" className="text-sm font-normal">
                Include partial responses in final score calculation (50%+ answered)
              </Label>
            </div>
          </CardContent>
        </Card>

        {/* Login Method */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Recipient Login Method</CardTitle>
            <CardDescription>
              How recipients will authenticate when they receive their invite email
            </CardDescription>
          </CardHeader>
          <CardContent>
            <RadioGroup
              value={formData.auth_method}
              onValueChange={(v) => setFormData({ ...formData, auth_method: v })}
              className="space-y-2"
            >
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${formData.auth_method === 'otp' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/50'}`}>
                <RadioGroupItem value="otp" className="mt-0.5" />
                <Shield className="h-4 w-4 mt-0.5 shrink-0 text-violet-500" />
                <div>
                  <p className="font-medium text-sm">One-Time Password (OTP)</p>
                  <p className="text-xs text-muted-foreground mt-0.5">A fresh code is emailed each time the recipient logs in. Most secure.</p>
                </div>
              </label>
              <label className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${formData.auth_method === 'password' ? 'border-primary bg-primary/5' : 'border-border hover:border-muted-foreground/50'}`}>
                <RadioGroupItem value="password" className="mt-0.5" />
                <KeyRound className="h-4 w-4 mt-0.5 shrink-0 text-slate-500" />
                <div>
                  <p className="font-medium text-sm">Temporary Password</p>
                  <p className="text-xs text-muted-foreground mt-0.5">System generates a password included in the invite email. Better for multi-session access.</p>
                </div>
              </label>
            </RadioGroup>
          </CardContent>
        </Card>

        {/* Recipients */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Recipients</CardTitle>
                <CardDescription>
                  Add staff members who will complete the assessment
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => setShowBulkInput(!showBulkInput)}>
                  Bulk Add
                </Button>
                <Button size="sm" onClick={addRecipient}>
                  <Plus className="h-4 w-4 mr-1" />
                  Add
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {showBulkInput && (
              <div className="space-y-2 p-4 bg-muted rounded-lg">
                <Label>Bulk Add Recipients</Label>
                <Textarea
                  placeholder="Enter emails (one per line, or comma-separated)&#10;Format: email, name, department&#10;e.g., john@example.com, John Doe, IT"
                  value={bulkInput}
                  onChange={(e) => setBulkInput(e.target.value)}
                  rows={4}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={parseBulkInput}>Add Recipients</Button>
                  <Button size="sm" variant="outline" onClick={() => setShowBulkInput(false)}>Cancel</Button>
                </div>
              </div>
            )}

            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email *</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipients.map((recipient, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Input
                        type="email"
                        placeholder="email@example.com"
                        value={recipient.email}
                        onChange={(e) => updateRecipient(index, 'email', e.target.value)}
                        className="h-9"
                        data-testid={`recipient-email-${index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Full name"
                        value={recipient.name}
                        onChange={(e) => updateRecipient(index, 'name', e.target.value)}
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        placeholder="Department"
                        value={recipient.department}
                        onChange={(e) => updateRecipient(index, 'department', e.target.value)}
                        className="h-9"
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removeRecipient(index)}
                        disabled={recipients.length === 1}
                      >
                        <Trash2 className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Users className="h-4 w-4" />
              <span>{validRecipientCount} valid recipient{validRecipientCount !== 1 ? 's' : ''}</span>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex justify-end gap-3 pb-8">
          <Button variant="outline" onClick={() => navigate('/compliance/questionnaires')}>
            Cancel
          </Button>
          <Button variant="outline" onClick={() => handleSave(false)} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            Save Draft
          </Button>
          <Button onClick={() => handleSave(true)} disabled={saving} data-testid="create-send-btn">
            <Send className="h-4 w-4 mr-2" />
            Create & Send
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
